import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    StyleSheet
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Timeline,
    Button,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    PlusIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "Timeline";

const USAGE_CODE = `<Timeline
    accessibilityLabel="Project activity"
>
    <Timeline.Item
        title={<>
            <Timeline.Emphasis>
                Ada Lovelace
            </Timeline.Emphasis>
            {" completed a task"}
        </>}
        avatar={{
            title: "Ada Lovelace"
        }}
        description="Login screen design"
        time="2026-10-24T12:32:00"
    />
    <Timeline.Item
        title="Due date missed"
        time="2026-10-23T09:00:00"
        type="warning"
    />
</Timeline>`;

const LOAD_MORE_CODE = `<Timeline
    isEndReached={items.length >= total}
    isLoadingMore={isLoadingMore}
    onLoadMore={handleLoadMore}
    accessibilityLabel="Activity"
    totalCount={total}
>
    {items.map((item) => <Timeline.Item
        title={item.title}
        time={item.time}
        key={item.id}
    />)}
</Timeline>`;

const TYPE_ITEMS = [
    {
        key: "timelinePage.paymentReceived",
        type: "success"
    },
    {
        key: "timelinePage.dueMissed",
        type: "warning"
    },
    {
        key: "timelinePage.paymentRejected",
        type: "danger"
    },
    {
        key: "timelinePage.releaseNotes",
        type: "info"
    }
] as const;

const TIME_BEHAVIOURS = [
    "auto",
    "relative",
    "absolute"
] as const;

const MINUTE = 60000;

const HOUR = 3600000;

const DAY = 86400000;

const TIME_OFFSETS = [
    30000,
    12 * MINUTE,
    3 * HOUR,
    3 * DAY,
    20 * DAY
];

const MAX_EVENTS = 3;

const FEED_TOTAL = 12;

const FEED_STEP = 3;

const LOAD_DELAY = 900;

const COLUMN_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexDirection: "column",
    display: "flex",
    gap: 12
};

const GRID_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex",
    gap: 24
};

const stylesheet = StyleSheet.create({
    timeline: {
        width: 240
    },
    state: {
        width: 260
    }
});

const getPastDate = (dayOffset: number, hours: number, minutes: number) => {
    const date = new Date();

    date.setDate(date.getDate() - dayOffset);

    date.setHours(hours, minutes, 0, 0);

    return date;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("timelinePage.markersTitle"),
        id: "markers"
    },
    {
        title: t("timelinePage.contentTitle"),
        id: "content"
    },
    {
        title: t("timelinePage.timeTitle"),
        id: "time"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("timelinePage.loadMoreTitle"),
        id: "load-more"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TimelinePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        now
    ] = useState(() => Date.now());

    const [
        events,
        setEvents
    ] = useState<Array<Date>>([]);

    const [
        openCount,
        setOpenCount
    ] = useState(0);

    const [
        feedCount,
        setFeedCount
    ] = useState(FEED_STEP);

    const [
        isLoadingMore,
        setIsLoadingMore
    ] = useState(false);

    useEffect(() => {
        if(!isLoadingMore) {
            return;
        }

        const timer = window.setTimeout(() => {
            setFeedCount((current) => Math.min(current + FEED_STEP, FEED_TOTAL));

            setIsLoadingMore(false);
        }, LOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoadingMore
    ]);

    const avatar = {
        title: t("sample.name")
    };

    const getTime = (offset: number) => new Date(now - offset);

    const handleAddEvent = () => setEvents((current) => [
        new Date(),
        ...current
    ].slice(0, MAX_EVENTS));

    const handleOpen = () => setOpenCount((current) => current + 1);

    const handleLoadMore = () => setIsLoadingMore(true);

    const handleNothing = () => undefined;

    const renderTitle = (name: string, text: string) => <>
        <Timeline.Emphasis>
            {name}
        </Timeline.Emphasis>
        {` ${text}`}
    </>;

    const renderMarkers = (size: "small" | "medium") => <Timeline
        accessibilityLabel={t("timelinePage.activityLabel")}
        style={stylesheet.timeline}
        size={size}
    >
        <Timeline.Item
            title={renderTitle(t("sample.name"), t("timelinePage.completedTask"))}
            time={getTime(2 * HOUR)}
            avatar={avatar}
        />
        <Timeline.Item
            title={renderTitle(t("timelinePage.sprint"), t("timelinePage.started"))}
            time={getTime(4 * HOUR)}
            icon={StarIcon}
        />
        <Timeline.Item
            title={renderTitle(t("timelinePage.personGrace"), t("timelinePage.viewedReport"))}
            time={getTime(6 * HOUR)}
            isDot={true}
        />
    </Timeline>;

    const renderTimeBehaviour = (timeBehaviour: typeof TIME_BEHAVIOURS[number]) => <div
        style={COLUMN_STYLE}
        key={timeBehaviour}
    >
        <InlineCode>
            {`timeBehaviour="${timeBehaviour}"`}
        </InlineCode>
        <Timeline
            accessibilityLabel={t("timelinePage.activityLabel")}
            timeBehaviour={timeBehaviour}
            style={stylesheet.timeline}
            size="small"
        >
            {
                TIME_OFFSETS.map((offset) => <Timeline.Item
                    title={t("timelinePage.sampleEvent")}
                    time={getTime(offset)}
                    key={offset}
                    isDot={true}
                />)
            }
        </Timeline>
    </div>;

    const renderState = (label: string, timeline: ReactNode) => <div
        style={COLUMN_STYLE}
        key={label}
    >
        <Text
            variant="labelMediumSize"
            color="mid"
        >
            {label}
        </Text>
        {timeline}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("timelinePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("timelinePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("timelinePage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("timelinePage.overviewCaption")}
                isStacked={true}
            >
                <Timeline
                    accessibilityLabel={t("timelinePage.activityLabel")}
                >
                    {
                        events.map((date) => <Timeline.Item
                            title={renderTitle(t("sample.name"), t("timelinePage.addedEvent"))}
                            key={date.getTime()}
                            avatar={avatar}
                            time={date}
                        />)
                    }
                    <Timeline.Item
                        title={renderTitle(t("sample.name"), t("timelinePage.completedTask"))}
                        description={t("timelinePage.loginDesign")}
                        time={getTime(12 * MINUTE)}
                        avatar={avatar}
                    />
                    <Timeline.Item
                        title={renderTitle(t("timelinePage.sprint"), t("timelinePage.started"))}
                        time={getTime(3 * HOUR)}
                        icon={PlusIcon}
                    />
                    <Timeline.Item
                        description={t("timelinePage.dueDescription")}
                        title={t("timelinePage.dueMissed")}
                        time={getTime(DAY + HOUR)}
                        type="warning"
                    />
                </Timeline>
                <Button
                    title={t("timelinePage.addEvent")}
                    onPress={handleAddEvent}
                    variant="outline"
                    icon={PlusIcon}
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("timelinePage.markersTitle")}
            id="markers"
        >
            <Paragraph>
                {t("timelinePage.markersText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    <div
                        style={COLUMN_STYLE}
                    >
                        <InlineCode>
                            avatar · icon · isDot
                        </InlineCode>
                        {renderMarkers("medium")}
                    </div>
                    <div
                        style={COLUMN_STYLE}
                    >
                        <InlineCode>
                            type
                        </InlineCode>
                        <Timeline
                            accessibilityLabel={t("timelinePage.activityLabel")}
                            style={stylesheet.timeline}
                        >
                            {
                                TYPE_ITEMS.map((item, index) => <Timeline.Item
                                    time={getTime((index + 1) * HOUR)}
                                    title={t(item.key)}
                                    type={item.type}
                                    key={item.type}
                                />)
                            }
                        </Timeline>
                    </div>
                    <div
                        style={COLUMN_STYLE}
                    >
                        <InlineCode>
                            size="small"
                        </InlineCode>
                        {renderMarkers("small")}
                    </div>
                </div>
            </Preview>
        </Section>
        <Section
            title={t("timelinePage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("timelinePage.contentText")}
            </Paragraph>
            <Preview
                caption={t("timelinePage.contentCaption", [
                    openCount
                ])}
                isStacked={true}
            >
                <Timeline
                    accessibilityLabel={t("timelinePage.activityLabel")}
                >
                    <Timeline.Item
                        title={renderTitle(t("sample.name"), t("timelinePage.commented"))}
                        time={getTime(12 * MINUTE)}
                        avatar={avatar}
                    >
                        <Timeline.Body>
                            {t("timelinePage.commentBody")}
                        </Timeline.Body>
                        <Timeline.Actions>
                            <Button
                                title={t("timelinePage.reply")}
                                onPress={handleNothing}
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </Timeline.Actions>
                    </Timeline.Item>
                    <Timeline.Item
                        title={<>
                            <Timeline.Emphasis>
                                {t("timelinePage.personGrace")}
                            </Timeline.Emphasis>
                            {` ${t("timelinePage.changedStatus")} `}
                            <Timeline.Link
                                onPress={handleNothing}
                            >
                                {t("timelinePage.screenName")}
                            </Timeline.Link>
                        </>}
                        time={getTime(2 * HOUR)}
                        type="success"
                    >
                        <Timeline.Change
                            from={{
                                title: t("timelinePage.inProgress")
                            }}
                            to={{
                                title: t("timelinePage.completed"),
                                type: "success"
                            }}
                        />
                    </Timeline.Item>
                    <Timeline.Item
                        title={renderTitle(t("timelinePage.personAlan"), t("timelinePage.addedFile"))}
                        attachments={[
                            {
                                description: t("sample.fileSize"),
                                title: t("sample.fileName"),
                                onPress: handleOpen
                            }
                        ]}
                        time={getTime(4 * HOUR)}
                    />
                    <Timeline.Item
                        description={t("timelinePage.trackingDescription")}
                        title={t("timelinePage.orderShipped")}
                        time={getTime(DAY - 2 * HOUR)}
                        onPress={handleOpen}
                    />
                </Timeline>
            </Preview>
        </Section>
        <Section
            title={t("timelinePage.timeTitle")}
            id="time"
        >
            <Paragraph>
                {t("timelinePage.timeText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    {TIME_BEHAVIOURS.map(renderTimeBehaviour)}
                </div>
            </Preview>
            <Paragraph>
                {t("timelinePage.groupedText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Timeline
                    accessibilityLabel={t("timelinePage.historyLabel")}
                    isGroupedByDay={true}
                    size="small"
                >
                    <Timeline.Item
                        title={t("timelinePage.orderDelivery")}
                        time={getTime(12 * MINUTE)}
                        isDot={true}
                    />
                    <Timeline.Item
                        title={t("timelinePage.orderShipped")}
                        time={getTime(50 * MINUTE)}
                        isDot={true}
                    />
                    <Timeline.Item
                        title={t("timelinePage.orderPrepared")}
                        time={getPastDate(1, 18, 30)}
                        isDot={true}
                    />
                    <Timeline.Item
                        title={t("timelinePage.orderPaid")}
                        time={getPastDate(1, 10, 5)}
                        isDot={true}
                    />
                    <Timeline.Item
                        title={t("timelinePage.orderCreated")}
                        time={getPastDate(4, 16, 20)}
                        isDot={true}
                    />
                </Timeline>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("timelinePage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={GRID_STYLE}
                >
                    {
                        renderState(t("timelinePage.stateLoading"), <Timeline
                            accessibilityLabel={t("timelinePage.activityLabel")}
                            style={stylesheet.state}
                            isLoading={true}
                        />)
                    }
                    {
                        renderState(t("timelinePage.stateEmpty"), <Timeline
                            accessibilityLabel={t("timelinePage.activityLabel")}
                            style={stylesheet.state}
                        />)
                    }
                    {
                        renderState(t("timelinePage.stateError"), <Timeline
                            accessibilityLabel={t("timelinePage.activityLabel")}
                            error={t("timelinePage.errorText")}
                            style={stylesheet.state}
                            onRetry={handleNothing}
                        />)
                    }
                    {
                        renderState(t("timelinePage.stateDenied"), <Timeline
                            accessibilityLabel={t("timelinePage.activityLabel")}
                            onRequestAccess={handleNothing}
                            isPermissionDenied={true}
                            style={stylesheet.state}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("timelinePage.loadMoreTitle")}
            id="load-more"
        >
            <Paragraph>
                {t("timelinePage.loadMoreText")}
            </Paragraph>
            <Preview
                caption={t("timelinePage.loadMoreCaption", [
                    FEED_TOTAL
                ])}
                isStacked={true}
            >
                <Timeline
                    accessibilityLabel={t("timelinePage.activityLabel")}
                    isEndReached={feedCount >= FEED_TOTAL}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={handleLoadMore}
                    totalCount={FEED_TOTAL}
                >
                    {
                        Array.from({
                            length: feedCount
                        }, (_, index) => <Timeline.Item
                            title={renderTitle(t("sample.name"), t("timelinePage.updatedBudget"))}
                            time={getTime((index + 1) * 3 * HOUR)}
                            avatar={avatar}
                            key={index}
                        />)
                    }
                </Timeline>
            </Preview>
            <CodeBlock
                code={LOAD_MORE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("timelinePage.a11yName"),
                    t("timelinePage.a11yMarkers"),
                    t("timelinePage.a11yTime"),
                    t("timelinePage.a11yDays"),
                    t("timelinePage.a11yFeed"),
                    t("timelinePage.a11yPress"),
                    t("timelinePage.a11yStates"),
                    t("timelinePage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("timelinePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("timelinePage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("timelinePage.propTimeBehaviour"),
                        type: "\"auto\" | \"relative\" | \"absolute\"",
                        defaultValue: "\"auto\"",
                        name: "timeBehaviour"
                    },
                    {
                        description: t("timelinePage.propIsGroupedByDay"),
                        name: "isGroupedByDay",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("timelinePage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("timelinePage.propError"),
                        type: "string | null",
                        defaultValue: "null",
                        name: "error"
                    },
                    {
                        description: t("timelinePage.propOnRetry"),
                        type: "() => void",
                        name: "onRetry"
                    },
                    {
                        description: t("timelinePage.propIsPermissionDenied"),
                        name: "isPermissionDenied",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("timelinePage.propOnRequestAccess"),
                        name: "onRequestAccess",
                        type: "() => void"
                    },
                    {
                        description: t("timelinePage.propEmptyContent"),
                        name: "emptyContent",
                        type: "ReactNode"
                    },
                    {
                        description: t("timelinePage.propOnLoadMore"),
                        type: "() => void",
                        name: "onLoadMore"
                    },
                    {
                        description: t("timelinePage.propIsLoadingMore"),
                        defaultValue: "false",
                        name: "isLoadingMore",
                        type: "boolean"
                    },
                    {
                        description: t("timelinePage.propIsEndReached"),
                        defaultValue: "false",
                        name: "isEndReached",
                        type: "boolean"
                    },
                    {
                        description: t("timelinePage.propTotalCount"),
                        name: "totalCount",
                        type: "number"
                    },
                    {
                        description: t("timelinePage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("timelinePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("timelinePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("timelinePage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("timelinePage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("timelinePage.propPartTitle"),
                        type: "ReactNode",
                        name: "title"
                    },
                    {
                        description: t("timelinePage.propPartTime"),
                        type: "Date | string",
                        name: "time"
                    },
                    {
                        description: t("timelinePage.propPartDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("timelinePage.propPartAvatar"),
                        type: "IAvatarProps",
                        name: "avatar"
                    },
                    {
                        description: t("timelinePage.propPartIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        type: "\"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("timelinePage.propPartType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("timelinePage.propPartIsDot"),
                        defaultValue: "false",
                        type: "boolean",
                        name: "isDot"
                    },
                    {
                        description: t("timelinePage.propPartAttachments"),
                        type: "Array<IAttachmentProps>",
                        name: "attachments"
                    },
                    {
                        description: t("timelinePage.propPartOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("timelinePage.propPartAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("timelinePage.propPartFrom"),
                        type: "{ title, type, icon }",
                        name: "from"
                    },
                    {
                        description: t("timelinePage.propPartTo"),
                        type: "{ title, type, icon }",
                        name: "to"
                    },
                    {
                        description: t("timelinePage.propPartHref"),
                        type: "string",
                        name: "href"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TimelinePage;
